
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// module components
import TemplatesActions from '@/modules/CreateOrderModule/components/TemplatesActions/index.vue'
import StepDate from '@/modules/EditOrderModule/components/StepDate/index.vue'
import OrderReceivers from '@/modules/EditOrderModule/components/OrderReceivers/index.vue'
import StepReceivers from '@/modules/CreateOrderModule/components/StepReceivers/index.vue'
import StepCargo from '@/modules/CreateOrderModule/components/StepCargo/index.vue'
import CompletedCargos from '@/modules/CreateOrderModule/components/CompletedCargos/index.vue'
import StepUpdate from '@/modules/EditOrderModule/components/StepUpdate/index.vue'

// types
import { ReceiverPayloadT, ReceiverT } from '@/core/types/receivers.types'
import { SenderPayloadT, SenderT } from '@/core/types/senders.types'

import { CargoT, CompletedCargoT } from '@/core/types/create_order.types'
import {
    FullOrderReceiverT,
    UpdateOrderReceiver,
    UpdateOrderPayloadI,
} from '@/core/types/orders.types'

// constants
import { EMPTY_ADDITIONAL_SERVICES } from '@/core/constants/additional_services.constants'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useActions from '@/common/composable/useActions'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import { useMaska } from '@/common/composable/useMaska'
import { senders } from '@/core/realisations/senders.realisation'
import {
    LTL_SHIPMENT_TYPES_OBJECT,
    PAYMENT_TYPES_OBJECT,
    PAYMENT_METHODS_OBJECT,
} from '@/core/constants/create_order.constants'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        loader: Loader,
        //
        'templates-actions': TemplatesActions,
        'step-date': StepDate,
        'order-receivers': OrderReceivers,
        'step-receivers': StepReceivers,
        'step-cargo': StepCargo,
        'completed-cargos': CompletedCargos,
        'step-update': StepUpdate,
    },
    setup() {
        const route = useRoute()
        const order_id = Number(route.params.order_id)

        const order_state = reactive({
            is_loading: false,
            is_failed: false,
            is_loaded: false,
            order: {
                comment: null as string | null,
                company: null as string | null,
                company_id: null as number | null,
                created_at: null as string | null,
                period: null as string | null,
                period_id: null as number | null,
                take_date: null as string | null,
                sender: {
                    id: null as number | null,
                    city: null as string | null,
                    city_id: null as number | null,
                    title: null as string | null,
                    full_address: null as string | null,
                    full_name: null as string | null,
                    phone: null as string | null,
                    additional_phone: null as string | null,
                    comment: null as string | null,
                    country: null as string | null,
                    country_id: null as number | null,
                    house: null as string | null,
                    street: null as string | null,
                    index: null as string | null,
                    office: null as string | null,
                    latitude: null as string | null,
                    longitude: null as string | null,
                    warehouse_id: null as number | null,
                },
                receivers: [] as FullOrderReceiverT[],
            },
        })

        const state = reactive({
            receiver: null as ReceiverT | null,
            completed_cargos: [] as CompletedCargoT[],
            // update cargo
            is_updating_cargo: false,
            is_updating_created_cargo: false,
            updating_cargo_index: null as number | null,
            updating_cargo_id: null as number | null,
            updating_cargo: null as CompletedCargoT | null,
            //
            is_updating_order: false,
            is_order_updated: false,
        })

        const notifications = useNotifications()

        const loadOrder = async () => {
            try {
                order_state.is_loading = true

                const response = await orders.getOrder(order_id)
                order_state.order = response.data

                order_state.is_failed = false
                order_state.is_loaded = true
            } catch (error) {
                console.error(error)

                order_state.is_failed = true
                order_state.is_loaded = false

                notifications.notifyError({
                    error,
                    title: 'При получении данных о заказе произошла ошибка',
                })
            } finally {
                order_state.is_loading = false
            }
        }

        onBeforeMount(loadOrder)

        const onReceiverSubmit = (receiver: ReceiverT) => {
            state.receiver = receiver
        }

        const onReceiverReset = () => {
            state.receiver = null
            state.completed_cargos = []
        }

        const cancelUpdateCargo = () => {
            state.is_updating_cargo = false
            state.is_updating_created_cargo = false

            state.updating_cargo_index = null
            state.updating_cargo_id = null
            state.updating_cargo = null

            state.receiver = null
        }

        const onCargoSubmit = (cargo: CargoT) => {
            if (!state.receiver) return

            if (state.is_updating_cargo) {
                if (state.updating_cargo_index === null) return

                if (state.is_updating_created_cargo) {
                    order_state.order.receivers[state.updating_cargo_index].cod_payment =
                        cargo.cod_payment
                    order_state.order.receivers[state.updating_cargo_index].declared_price =
                        cargo.declared_price ? cargo.declared_price : 0
                    order_state.order.receivers[state.updating_cargo_index].product_name =
                        cargo.product_name
                    order_state.order.receivers[state.updating_cargo_index].dop_invoice_number =
                        cargo.customer_invoice_number
                    order_state.order.receivers[state.updating_cargo_index].annotation =
                        cargo.annotation
                    order_state.order.receivers[state.updating_cargo_index].depth = cargo.depth
                    order_state.order.receivers[state.updating_cargo_index].width = cargo.width
                    order_state.order.receivers[state.updating_cargo_index].height = cargo.height
                    order_state.order.receivers[state.updating_cargo_index].volume = cargo.volume
                    order_state.order.receivers[state.updating_cargo_index].places = cargo.places
                    order_state.order.receivers[state.updating_cargo_index].weight = cargo.weight

                    order_state.order.receivers[state.updating_cargo_index].shipment_type_id =
                        cargo.shipment_type
                    order_state.order.receivers[state.updating_cargo_index].payment_method_id =
                        cargo.payment_method
                    order_state.order.receivers[state.updating_cargo_index].payment_type_id =
                        cargo.payment_type

                    const shipment_id = cargo.shipment_type.toString() as '1' | '2'
                    const payment_type_id = cargo.payment_type.toString() as '1' | '2'
                    const payment_method_id = cargo.payment_method.toString() as '3' | '2'

                    const shipment_type = LTL_SHIPMENT_TYPES_OBJECT[shipment_id]
                    const payment_type = PAYMENT_TYPES_OBJECT[payment_type_id]
                    const payment_method = PAYMENT_METHODS_OBJECT[payment_method_id]

                    order_state.order.receivers[state.updating_cargo_index].shipment_type =
                        shipment_type
                    order_state.order.receivers[state.updating_cargo_index].payment_type =
                        payment_type
                    order_state.order.receivers[state.updating_cargo_index].payment_method =
                        payment_method

                    order_state.order.receivers[state.updating_cargo_index].payer_company_id =
                        cargo.payer_company_id
                } else {
                    state.completed_cargos[state.updating_cargo_index].receiver = state.receiver
                    state.completed_cargos[state.updating_cargo_index].logistics_info = cargo
                }

                cancelUpdateCargo()
                return
            }

            const completed_cargo: CompletedCargoT = {
                receiver: state.receiver,
                logistics_info: cargo,
            }

            state.completed_cargos.unshift(completed_cargo)
            state.receiver = null
        }

        const onDeleteCargo = (completed_cargo_index: number) => {
            state.completed_cargos.splice(completed_cargo_index, 1)
        }

        const onStartUpdateCargo = (completed_cargo_index: number) => {
            const completed_cargo = state.completed_cargos[completed_cargo_index]
            if (!completed_cargo) return

            state.updating_cargo_index = completed_cargo_index
            state.updating_cargo = completed_cargo

            state.is_updating_cargo = true
            state.is_updating_created_cargo = false

            state.receiver = completed_cargo.receiver
        }

        const resetOrder = () => {
            state.is_order_updated = false

            state.updating_cargo = null
            state.updating_cargo_index = null
            state.is_updating_cargo = false

            state.completed_cargos = []

            state.receiver = null

            order_state.is_loaded = false
            order_state.order.comment = null
            order_state.order.company = null
            order_state.order.created_at = null
            order_state.order.period = null
            order_state.order.period_id = null
            order_state.order.take_date = null

            order_state.order.sender.id = null
            order_state.order.sender.city = null
            order_state.order.sender.city_id = null
            order_state.order.sender.title = null
            order_state.order.sender.full_address = null
            order_state.order.sender.full_name = null
            order_state.order.sender.phone = null
            order_state.order.sender.additional_phone = null
            order_state.order.sender.house = null
            order_state.order.sender.street = null
            order_state.order.sender.index = null
            order_state.order.sender.office = null
            order_state.order.sender.latitude = null
            order_state.order.sender.longitude = null
            order_state.order.sender.warehouse_id = null
            order_state.order.sender.comment = null
            order_state.order.sender.country = null
            order_state.order.sender.country_id = null

            order_state.order.receivers = []

            loadOrder()
        }

        const { unmaskNumbers } = useMaska()

        const onSubmitOrder = async () => {
            if (
                !order_id ||
                !order_state.order ||
                !order_state.order.take_date ||
                !order_state.order.period_id
            ) {
                return
            }

            const take_date = order_state.order.take_date
            const period_id = order_state.order.period_id

            try {
                state.is_updating_order = true

                const receivers: UpdateOrderReceiver[] = state.completed_cargos.map(
                    (completed_cargo) => {
                        const cod_payment = completed_cargo.logistics_info.cod_payment
                            ? completed_cargo.logistics_info.cod_payment
                            : '0'
                        const declared_price = completed_cargo.logistics_info.declared_price
                            ? completed_cargo.logistics_info.declared_price
                            : '0'

                        return {
                            receiver_id: completed_cargo.receiver.id,
                            order_logistics_info: {
                                take_date,
                                period_id,
                                product_name: completed_cargo.logistics_info.product_name,
                                dop_invoice_number:
                                    completed_cargo.logistics_info.customer_invoice_number,
                                annotation: completed_cargo.logistics_info.annotation,
                                cod_payment: Number(unmaskNumbers(cod_payment)),
                                declared_price: Number(unmaskNumbers(declared_price)),
                                places: completed_cargo.logistics_info.places,
                                volume: completed_cargo.logistics_info.volume,
                                weight: completed_cargo.logistics_info.weight,
                                payment_type: completed_cargo.logistics_info.payment_type,
                                payment_method: completed_cargo.logistics_info.payment_method,
                                shipment_type: completed_cargo.logistics_info.shipment_type,
                                height: completed_cargo.logistics_info.height,
                                width: completed_cargo.logistics_info.width,
                                depth: completed_cargo.logistics_info.depth,
                                should_return_document:
                                    completed_cargo.logistics_info.is_return_documents,
                            },
                            additional_service: EMPTY_ADDITIONAL_SERVICES,
                        }
                    }
                )

                const payload: UpdateOrderPayloadI = {
                    receivers,
                }

                await orders.updateOrder(order_id, payload)

                notifications.notify({
                    title: 'Заказ отредактирован',
                    type: 'success',
                })

                state.is_order_updated = true
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании заказа произошла ошбика',
                })
            } finally {
                state.is_updating_order = false
            }
        }

        const { actions_state, startUpdate } = useActions<SenderT>()

        const startSenderUpdate = () => {
            if (
                !order_state.order.sender.id ||
                !order_state.order.sender.city ||
                !order_state.order.sender.city_id ||
                !order_state.order.sender.country ||
                !order_state.order.sender.country_id ||
                !order_state.order.sender.full_address ||
                !order_state.order.sender.full_name ||
                !order_state.order.sender.latitude ||
                !order_state.order.sender.longitude ||
                !order_state.order.sender.phone ||
                !order_state.order.sender.title
            ) {
                return
            }

            const sender: SenderT = {
                id: order_state.order.sender.id,
                city: order_state.order.sender.city,
                city_id: order_state.order.sender.city_id,
                comment: order_state.order.sender.comment,
                country: order_state.order.sender.country,
                country_id: order_state.order.sender.country_id,
                full_address: order_state.order.sender.full_address,
                full_name: order_state.order.sender.full_name,
                house: order_state.order.sender.house,
                index: order_state.order.sender.index,
                latitude: order_state.order.sender.latitude,
                longitude: order_state.order.sender.longitude,
                office: order_state.order.sender.office,
                phone: order_state.order.sender.phone,
                additional_phone: order_state.order.sender.additional_phone,
                street: order_state.order.sender.street,
                title: order_state.order.sender.title,
                warehouse_id: order_state.order.sender.warehouse_id,
            }

            startUpdate(sender)
        }

        const onSenderUpdate = async (payload: SenderPayloadT) => {
            try {
                actions_state.is_actions_loading = true

                if (!payload.id || !payload.latitude || !payload.longitude) return

                await senders.updateSenderInOrder(payload.id, payload)

                order_state.order.sender.title = payload.title
                order_state.order.sender.city_id = payload.city_id
                order_state.order.sender.comment = payload.comment
                order_state.order.sender.full_address = payload.full_address
                order_state.order.sender.full_name = payload.full_name
                order_state.order.sender.house = payload.house
                order_state.order.sender.index = payload.index
                order_state.order.sender.latitude = payload.latitude.toString()
                order_state.order.sender.longitude = payload.longitude.toString()
                order_state.order.sender.office = payload.office
                order_state.order.sender.phone = payload.phone
                order_state.order.sender.additional_phone = payload.additional_phone
                order_state.order.sender.street = payload.street

                notifications.notify({ title: 'Отправитель отредактирован', type: 'success' })

                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при редактировании отправителя' })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const onDateReset = () => {
            order_state.order.take_date = null
            order_state.order.period = null
            order_state.order.period_id = null
        }

        const onDateSubmit = (date: string) => {
            order_state.order.take_date = date
        }

        const onStartUpdateCreatedCargo = (created_cargo_index: number) => {
            const created_cargo = order_state.order.receivers[created_cargo_index]
            if (!created_cargo) return

            state.updating_cargo_id = created_cargo.id

            const cargo: CompletedCargoT = {
                receiver: {
                    id: created_cargo.receiver_id,
                    city: created_cargo.city,
                    city_id: created_cargo.city_id,
                    comment: created_cargo.comment,
                    entity: created_cargo.title,
                    title: created_cargo.title,
                    country: created_cargo.country,
                    country_id: created_cargo.country_id,
                    full_address: created_cargo.full_address,
                    full_name: created_cargo.full_name,
                    house: created_cargo.house,
                    index: created_cargo.index,
                    latitude: created_cargo.latitude,
                    longitude: created_cargo.longitude,
                    office: created_cargo.office,
                    phone: created_cargo.phone,
                    additional_phone: created_cargo.additional_phone,
                    street: created_cargo.street,
                    warehouse_id: created_cargo.warehouse_id,
                },
                logistics_info: {
                    additional_services: EMPTY_ADDITIONAL_SERVICES,
                    product_name: created_cargo.product_name,
                    customer_invoice_number: created_cargo.dop_invoice_number,
                    weight: created_cargo.weight,
                    places: created_cargo.places,
                    depth: created_cargo.depth,
                    width: created_cargo.width,
                    height: created_cargo.height,
                    volume: created_cargo.volume,
                    annotation: created_cargo.annotation,
                    is_return_documents: created_cargo.should_return_document,
                    cod_payment: created_cargo.cod_payment,
                    cargo_name: created_cargo.cargo_name,
                    declared_price: created_cargo.declared_price.toString(),
                    shipment_type: created_cargo.shipment_type_id,
                    payment_method: created_cargo.payment_method_id,
                    payment_type: created_cargo.payment_type_id,
                    payer_company_id: created_cargo.payer_company_id,
                    verify: created_cargo.verify,
                    temperature_regime_type_id: created_cargo.temperature_regime_type_id,
                    // NOT FINISHED
                    is_dangerous: 0,
                    invoice_files: [],
                    certificate_of_safety_files: [],
                    temperature_regime_safety_files: [],
                },
            }

            state.updating_cargo_index = created_cargo_index
            state.updating_cargo = cargo

            state.is_updating_created_cargo = true
            state.is_updating_cargo = true

            state.receiver = cargo.receiver
        }

        const onReceiverUpdated = ({ receiver }: { receiver: ReceiverPayloadT }) => {
            const receiver_index = order_state.order.receivers.findIndex(
                (order_receiver) => order_receiver.receiver_id === receiver.id
            )

            if (
                !receiver.latitude ||
                !receiver.longitude ||
                !state.receiver ||
                receiver_index === -1
            ) {
                return
            }

            order_state.order.receivers[receiver_index].city_id = receiver.city_id
            order_state.order.receivers[receiver_index].index = receiver.index
            order_state.order.receivers[receiver_index].latitude = receiver.latitude.toString()
            order_state.order.receivers[receiver_index].longitude = receiver.longitude.toString()
            order_state.order.receivers[receiver_index].office = receiver.office
            order_state.order.receivers[receiver_index].phone = receiver.phone
            order_state.order.receivers[receiver_index].street = receiver.street
            order_state.order.receivers[receiver_index].title = receiver.title
            order_state.order.receivers[receiver_index].warehouse_id = receiver.warehouse_id
            order_state.order.receivers[receiver_index].full_address = receiver.full_address
            order_state.order.receivers[receiver_index].full_name = receiver.full_name
            order_state.order.receivers[receiver_index].house = receiver.house

            state.receiver.city_id = receiver.city_id
            state.receiver.index = receiver.index
            state.receiver.latitude = receiver.latitude.toString()
            state.receiver.longitude = receiver.longitude.toString()
            state.receiver.office = receiver.office
            state.receiver.phone = receiver.phone
            state.receiver.street = receiver.street
            state.receiver.title = receiver.title
            state.receiver.warehouse_id = receiver.warehouse_id
            state.receiver.full_address = receiver.full_address
            state.receiver.full_name = receiver.full_name
            state.receiver.house = receiver.house
        }

        // computeds
        const isReceiverSelected = computed(() => Boolean(state.receiver))

        const selectedReceiversIds = computed(() => {
            return state.completed_cargos.map((completed_cargo) => completed_cargo.receiver.id)
        })

        return {
            state,
            order_state,
            loadOrder,
            //
            onReceiverSubmit,
            onReceiverReset,
            //
            onCargoSubmit,
            onDeleteCargo,
            onStartUpdateCargo,
            cancelUpdateCargo,
            //
            resetOrder,
            onSubmitOrder,
            //
            actions_state,
            startUpdate,
            startSenderUpdate,
            onSenderUpdate,
            onDateReset,
            onDateSubmit,
            onStartUpdateCreatedCargo,
            onReceiverUpdated,
            //
            isReceiverSelected,
            selectedReceiversIds,
        }
    },
})
